import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Hotel as HotelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Payment as PaymentIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Services API
import locationService from '../../../services/locationService';
import chambreService from '../../../services/chambreService';

// Helper functions pour datetime-local - DÉFINIES AVANT LE COMPOSANT
const formatDateTimeLocal = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const parseDateTimeLocal = (datetimeString) => {
  if (!datetimeString) return new Date();
  return new Date(datetimeString);
};

const formatDateDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Filter states
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    statut: '',
    type_location: '',
    chambre: ''
  });

  // Form states
  const [formData, setFormData] = useState({
    chambre: '',
    nom_client: '',
    telephone_client: '',
    type_location: 'moment',
    prix_convenu: '',
    notes: '',
    heure_debut: formatDateTimeLocal(new Date())
  });

  const [checkoutData, setCheckoutData] = useState({
    heure_fin: formatDateTimeLocal(new Date()),
    montant_services: '0',
    montant_paye: '0'
  });

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load locations
      const response = await locationService.getAll();
      console.log('API Response for locations:', response);
      
      // Gérer différents formats de réponse
      let locationData = [];
      if (Array.isArray(response)) {
        locationData = response;
      } else if (response && Array.isArray(response.data)) {
        locationData = response.data;
      } else if (response && response.results && Array.isArray(response.results)) {
        locationData = response.results; // Django REST Framework pagination
      } else if (response && typeof response === 'object') {
        // Si c'est un objet unique, le mettre dans un tableau
        locationData = [response];
      }
      
      console.log('Processed locations:', locationData);
      setLocations(locationData);
      
      // Load available chambres
      const chambreResponse = await chambreService.getAvailable();
      console.log('API Response for chambres:', chambreResponse);
      
      let chambreData = [];
      if (Array.isArray(chambreResponse)) {
        chambreData = chambreResponse;
      } else if (chambreResponse && Array.isArray(chambreResponse.data)) {
        chambreData = chambreResponse.data;
      } else if (chambreResponse && chambreResponse.results && Array.isArray(chambreResponse.results)) {
        chambreData = chambreResponse.results;
      } else if (chambreResponse && typeof chambreResponse === 'object') {
        chambreData = [chambreResponse];
      }
      
      console.log('Processed chambres:', chambreData);
      setChambres(chambreData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // S'assurer que locations est toujours un tableau
  const safeLocations = Array.isArray(locations) ? locations : [];

  // Filter locations
  const filteredLocations = safeLocations.filter(location => {
    if (!location) return false;
    
    const matchesSearch = searchTerm === '' || 
      (location.nom_client && location.nom_client.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (location.chambre?.numero && location.chambre.numero.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (location.telephone_client && location.telephone_client.includes(searchTerm));
    
    const matchesStatut = !filters.statut || location.statut === filters.statut;
    const matchesType = !filters.type_location || location.type_location === filters.type_location;
    const matchesChambre = !filters.chambre || location.chambre?.id === parseInt(filters.chambre);
    
    return matchesSearch && matchesStatut && matchesType && matchesChambre;
  });

  // Group locations by status
  const locationsEnCours = filteredLocations.filter(l => l && l.statut === 'en_cours');
  const locationsTerminees = filteredLocations.filter(l => l && l.statut === 'terminee');
  const locationsAnnulees = filteredLocations.filter(l => l && l.statut === 'annulee');

  // Handler functions
  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setFormData({
        chambre: location.chambre?.id || '',
        nom_client: location.nom_client || '',
        telephone_client: location.telephone_client || '',
        type_location: location.type_location || 'moment',
        prix_convenu: location.prix_convenu || '',
        notes: location.notes || '',
        heure_debut: location.heure_debut ? formatDateTimeLocal(location.heure_debut) : formatDateTimeLocal(new Date())
      });
    } else {
      setSelectedLocation(null);
      setFormData({
        chambre: '',
        nom_client: '',
        telephone_client: '',
        type_location: 'moment',
        prix_convenu: '',
        notes: '',
        heure_debut: formatDateTimeLocal(new Date())
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLocation(null);
  };

  const handleOpenCheckoutDialog = (location) => {
    setSelectedLocation(location);
    setCheckoutData({
      heure_fin: formatDateTimeLocal(new Date()),
      montant_services: '0',
      montant_paye: '0'
    });
    setOpenCheckoutDialog(true);
  };

  const handleCloseCheckoutDialog = () => {
    setOpenCheckoutDialog(false);
    setSelectedLocation(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckoutInputChange = (e) => {
    const { name, value } = e.target;
    setCheckoutData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Prepare data for API
      const submitData = {
        ...formData,
        heure_debut: parseDateTimeLocal(formData.heure_debut).toISOString()
      };
      
      if (selectedLocation) {
        // Update existing location
        await locationService.update(selectedLocation.id, submitData);
        setSuccessMessage('Location modifiée avec succès');
      } else {
        // Create new location
        await locationService.create(submitData);
        setSuccessMessage('Location créée avec succès');
      }
      
      await loadData();
      handleCloseDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error saving location:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const checkoutPayload = {
        heure_fin: parseDateTimeLocal(checkoutData.heure_fin).toISOString(),
        montant_services: parseFloat(checkoutData.montant_services) || 0,
        montant_paye: parseFloat(checkoutData.montant_paye) || 0
      };
      
      await locationService.checkout(selectedLocation.id, checkoutPayload);
      setSuccessMessage('Check-out effectué avec succès');
      
      await loadData();
      handleCloseCheckoutDialog();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error during checkout:', err);
      setError(err.response?.data?.message || 'Erreur lors du check-out');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette location ?')) {
      try {
        setLoading(true);
        setError(null);
        
        await locationService.update(id, { statut: 'annulee' });
        setSuccessMessage('Location annulée avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelLocation = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cette location ?')) {
      try {
        setLoading(true);
        setError(null);
        
        await locationService.cancel(id);
        setSuccessMessage('Location annulée avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de l\'annulation');
      } finally {
        setLoading(false);
      }
    }
  };

  // Utility functions
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 HTG';
    return new Intl.NumberFormat('fr-HT', {
      style: 'currency',
      currency: 'HTG',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h${m}m` : `${h}h`;
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'en_cours': return 'warning';
      case 'terminee': return 'success';
      case 'annulee': return 'error';
      default: return 'default';
    }
  };

  const getStatutText = (statut) => {
    switch (statut) {
      case 'en_cours': return 'En cours';
      case 'terminee': return 'Terminée';
      case 'annulee': return 'Annulée';
      default: return statut;
    }
  };

  const calculateDureeActuelle = (heure_debut) => {
    if (!heure_debut) return 0;
    const start = new Date(heure_debut);
    const now = new Date();
    const diffMs = now - start;
    return diffMs / (1000 * 60 * 60); // Convert to hours
  };

  // Render location card
  const renderLocationCard = (location) => {
    if (!location) return null;
    
    const dureeActuelle = calculateDureeActuelle(location.heure_debut);
    
    return (
      <Card key={location.id || Math.random()} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                <HotelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Chambre {location.chambre?.numero || 'N/A'}
              </Typography>
              
              <Stack spacing={1}>
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    {location.nom_client || 'Client non renseigné'}
                  </Typography>
                </Box>
                
                {location.telephone_client && (
                  <Box display="flex" alignItems="center">
                    <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{location.telephone_client}</Typography>
                  </Box>
                )}
                
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    Début: {formatDateDisplay(location.heure_debut)}
                  </Typography>
                </Box>
                
                {location.heure_fin && (
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      Fin: {formatDateDisplay(location.heure_fin)}
                    </Typography>
                  </Box>
                )}
                
                <Box display="flex" alignItems="center">
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold">
                    Total: {formatCurrency(location.montant_total || location.prix_convenu || 0)}
                  </Typography>
                </Box>
                
                {location.montant_paye > 0 && (
                  <Box display="flex" alignItems="center">
                    <PaymentIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2" color="success.main">
                      Payé: {formatCurrency(location.montant_paye)}
                    </Typography>
                  </Box>
                )}
                
                {location.reste_a_payer > 0 && (
                  <Typography variant="body2" color="error">
                    Reste: {formatCurrency(location.reste_a_payer)}
                  </Typography>
                )}
              </Stack>
            </Box>
            
            <Box sx={{ minWidth: 100, textAlign: 'right' }}>
              <Chip
                label={getStatutText(location.statut)}
                color={getStatutColor(location.statut)}
                size="small"
                sx={{ mb: 1 }}
              />
              
              {location.statut === 'en_cours' && (
                <Typography variant="caption" display="block" color="warning.main">
                  Durée: {formatDuration(dureeActuelle)}
                </Typography>
              )}
              
              <Typography variant="caption" display="block" color="text.secondary">
                Type: {location.type_location || 'moment'}
              </Typography>
            </Box>
          </Box>
          
          {location.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              <InfoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {location.notes}
            </Typography>
          )}
        </CardContent>
        
        <CardActions>
          {location.statut === 'en_cours' && (
            <Button
              size="small"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleOpenCheckoutDialog(location)}
            >
              Check-out
            </Button>
          )}
          
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleOpenDialog(location)}
          >
            Modifier
          </Button>
          
          {location.statut === 'en_cours' ? (
            <Button
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleCancelLocation(location.id)}
            >
              Annuler
            </Button>
          ) : (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(location.id)}
            >
              Supprimer
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  // Loading state
  if (loading && safeLocations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestion des Locations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Nouvelle Location
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
            <Typography color="text.secondary" variant="body2">En cours</Typography>
            <Typography variant="h4" color="warning.dark">
              {locationsEnCours.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
            <Typography color="text.secondary" variant="body2">Terminées</Typography>
            <Typography variant="h4" color="success.dark">
              {locationsTerminees.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography color="text.secondary" variant="body2">Annulées</Typography>
            <Typography variant="h4" color="error.dark">
              {locationsAnnulees.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
            <Typography color="text.secondary" variant="body2">Total</Typography>
            <Typography variant="h4" color="primary.dark">
              {filteredLocations.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher client, chambre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.statut}
                onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                label="Statut"
                disabled={loading}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="en_cours">En cours</MenuItem>
                <MenuItem value="terminee">Terminées</MenuItem>
                <MenuItem value="annulee">Annulées</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type_location}
                onChange={(e) => setFilters({ ...filters, type_location: e.target.value })}
                label="Type"
                disabled={loading}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="moment">Moment</MenuItem>
                <MenuItem value="heure">Par heure</MenuItem>
                <MenuItem value="nuit">Par nuit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Chambre</InputLabel>
              <Select
                value={filters.chambre}
                onChange={(e) => setFilters({ ...filters, chambre: e.target.value })}
                label="Chambre"
                disabled={loading}
              >
                <MenuItem value="">Toutes</MenuItem>
                {Array.isArray(chambres) && chambres.map((chambre) => (
                  <MenuItem key={chambre.id} value={chambre.id}>
                    Chambre {chambre.numero}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilters({ statut: '', type_location: '', chambre: '' })}
                disabled={loading}
              >
                Réinitialiser
              </Button>
              <Tooltip title="Actualiser">
                <IconButton onClick={loadData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge badgeContent={locationsEnCours.length} color="warning" showZero>
                En cours
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={locationsTerminees.length} color="success" showZero>
                Terminées
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={locationsAnnulees.length} color="error" showZero>
                Annulées
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={filteredLocations.length} color="primary" showZero>
                Toutes
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Locations List */}
      <Box>
        {currentTab === 0 && (
          <>
            {locationsEnCours.length === 0 ? (
              <Alert severity="info">Aucune location en cours</Alert>
            ) : (
              locationsEnCours.map(renderLocationCard)
            )}
          </>
        )}
        
        {currentTab === 1 && (
          <>
            {locationsTerminees.length === 0 ? (
              <Alert severity="info">Aucune location terminée</Alert>
            ) : (
              locationsTerminees.map(renderLocationCard)
            )}
          </>
        )}
        
        {currentTab === 2 && (
          <>
            {locationsAnnulees.length === 0 ? (
              <Alert severity="info">Aucune location annulée</Alert>
            ) : (
              locationsAnnulees.map(renderLocationCard)
            )}
          </>
        )}
        
        {currentTab === 3 && (
          <>
            {filteredLocations.length === 0 ? (
              <Alert severity="info">Aucune location trouvée</Alert>
            ) : (
              filteredLocations.map(renderLocationCard)
            )}
          </>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedLocation ? 'Modifier la location' : 'Nouvelle location'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Chambre</InputLabel>
                  <Select
                    name="chambre"
                    value={formData.chambre}
                    onChange={handleInputChange}
                    label="Chambre"
                    disabled={!!selectedLocation || loading}
                  >
                    <MenuItem value="">Sélectionner une chambre</MenuItem>
                    {Array.isArray(chambres) && chambres.map((chambre) => (
                      <MenuItem key={chambre.id} value={chambre.id}>
                        Chambre {chambre.numero} - {chambre.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type de location</InputLabel>
                  <Select
                    name="type_location"
                    value={formData.type_location}
                    onChange={handleInputChange}
                    label="Type de location"
                    disabled={loading}
                  >
                    <MenuItem value="moment">Moment (forfait)</MenuItem>
                    <MenuItem value="heure">Par heure</MenuItem>
                    <MenuItem value="nuit">Par nuit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom_client"
                  label="Nom du client"
                  value={formData.nom_client}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="telephone_client"
                  label="Téléphone"
                  value={formData.telephone_client}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="heure_debut"
                  label="Date et heure de début"
                  type="datetime-local"
                  value={formData.heure_debut}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 minutes
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prix_convenu"
                  label="Prix convenu (HTG)"
                  type="number"
                  value={formData.prix_convenu}
                  onChange={handleInputChange}
                  InputProps={{ inputProps: { min: 0, step: 100 } }}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (selectedLocation ? 'Modifier' : 'Créer')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={openCheckoutDialog} onClose={handleCloseCheckoutDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleCheckout}>
          <DialogTitle>
            Check-out - Chambre {selectedLocation?.chambre?.numero}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Client: {selectedLocation?.nom_client || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      Durée actuelle: {formatDuration(calculateDureeActuelle(selectedLocation?.heure_debut))}
                    </Typography>
                    <Typography variant="body2">
                      Prix convenu: {formatCurrency(selectedLocation?.prix_convenu || 0)}
                    </Typography>
                  </Box>
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="heure_fin"
                  label="Date et heure de fin"
                  type="datetime-local"
                  value={checkoutData.heure_fin}
                  onChange={handleCheckoutInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 minutes
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="montant_services"
                  label="Services supplémentaires (HTG)"
                  type="number"
                  value={checkoutData.montant_services}
                  onChange={handleCheckoutInputChange}
                  InputProps={{ inputProps: { min: 0, step: 100 } }}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="montant_paye"
                  label="Montant payé (HTG)"
                  type="number"
                  value={checkoutData.montant_paye}
                  onChange={handleCheckoutInputChange}
                  InputProps={{ inputProps: { min: 0, step: 100 } }}
                  required
                  disabled={loading}
                />
              </Grid>
              
              {selectedLocation?.reste_a_payer > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Reste à payer: {formatCurrency(selectedLocation.reste_a_payer)}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCheckoutDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" color="success" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Finaliser le check-out'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Locations;